import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { Control, Controller } from 'react-hook-form';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Pagination,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import { TourReviewFilters, useClearTourReviewRating, useGetTourReviews } from '~/hooks/apis/reviews/useTourReviews';

import { REVIEWS_PER_PAGE } from '~/services/ReviewService';

import NativeTourCard from './NativeTourCard';
import NativeTourRelatedList from './NativeTourRelatedList';
import NativeTourReviewCard from './NativeTourReviewCard';
import { NativeTourForm } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
  tourOfferId: string;
  legacyTourId: string;
}

const FIRST_PAGE: TourReviewFilters = { page: 1, showHidden: true, showLeReviews: true, showExternalReviews: false };

/** The tour's reviews: which tours it shares them with, its rating, and each review. */
export default function NativeTourReviewsSection({ control, tourOfferId, legacyTourId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const tourId = `tour-${legacyTourId}`;
  const [filters, setFilters] = useState<TourReviewFilters>(FIRST_PAGE);
  const { data, isLoading, isFetching, error } = useGetTourReviews(tourId, filters);
  const clearRating = useClearTourReviewRating(tourId);
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);
  const reviews = data?.reviews ?? [];
  const pages = Math.ceil((data?.total ?? 0) / REVIEWS_PER_PAGE);

  const filter = (change: Partial<TourReviewFilters>) => setFilters({ ...filters, ...change, page: 1 });

  return (
    <Stack spacing={3}>
      <NativeTourCard
        title="Shared reviews"
        description="This tour also shows these tours' reviews, and its rating counts them. Saved with the page."
      >
        <Controller
          control={control}
          name="relationships.sharedReviews"
          render={({ field }) => (
            <NativeTourRelatedList
              value={field.value}
              onChange={field.onChange}
              selfId={tourOfferId}
              addLabel="Share reviews with a tour"
              emptyText="Only this tour's own reviews."
            />
          )}
        />
      </NativeTourCard>

      <NativeTourCard
        title="Rating"
        description="Clear the rating after changing reviews, so the tour page shows the new one."
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" disabled={clearRating.isPending} onClick={() => setIsConfirmingClear(true)}>
            Clear rating
          </Button>
          <Typography variant="body2" color="text.secondary">
            It's worked out again the next time a customer sees the tour.
          </Typography>
        </Stack>
      </NativeTourCard>

      <NativeTourCard title="Reviews" description="This tour's reviews and its shared tours'.">
        <Stack spacing={2}>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Chip
              label="Luxury Escapes"
              color={filters.showLeReviews ? 'primary' : 'default'}
              variant={filters.showLeReviews ? 'filled' : 'outlined'}
              onClick={() => filter({ showLeReviews: !filters.showLeReviews })}
            />
            <Chip
              label="External"
              color={filters.showExternalReviews ? 'primary' : 'default'}
              variant={filters.showExternalReviews ? 'filled' : 'outlined'}
              onClick={() => filter({ showExternalReviews: !filters.showExternalReviews })}
            />
            <FormControlLabel
              sx={{ ml: 1 }}
              control={
                <Switch
                  checked={filters.showHidden}
                  onChange={(event) => filter({ showHidden: event.target.checked })}
                />
              }
              label="Include hidden"
            />
            {isFetching && !isLoading && <CircularProgress size={16} />}
            {data && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {data.total} review{data.total === 1 ? '' : 's'}
              </Typography>
            )}
          </Stack>

          {isLoading && (
            <Box p={2} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          )}
          {error && <Alert severity="error">Couldn't load the reviews: {error.message}</Alert>}
          {!isLoading && !error && reviews.length === 0 && (
            <Typography color="text.secondary">No reviews match.</Typography>
          )}
          {reviews.map((review) => (
            <NativeTourReviewCard key={review.id} tourId={tourId} review={review} />
          ))}
          {pages > 1 && (
            <Pagination
              count={pages}
              page={filters.page}
              onChange={(_event, page) => setFilters({ ...filters, page })}
              sx={{ alignSelf: 'center' }}
            />
          )}
        </Stack>
      </NativeTourCard>

      <Dialog open={isConfirmingClear} onClose={() => setIsConfirmingClear(false)}>
        <DialogTitle>Clear the rating?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The tour page shows no rating until a customer next opens it, when it's worked out again from the reviews.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsConfirmingClear(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={clearRating.isPending}
            onClick={async () => {
              try {
                await clearRating.mutateAsync();
                enqueueSnackbar('Rating cleared', { variant: 'success' });
                setIsConfirmingClear(false);
              } catch (clearError) {
                enqueueSnackbar(`Couldn't clear the rating: ${clearError.message}`, { variant: 'error' });
              }
            }}
          >
            Clear rating
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
